import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { NavItemLink } from "@/components/Layout/BaseLayouts/Footer/NavItems/NavItemLink";
import { portalRoutes } from "@/router/routes/portalRoutes";

import { Collapsible } from "./Collapsible/Collapsible";

const isPartnerWelcomePath = (pathname: string) =>
  pathname === portalRoutes.welcomePartner ||
  pathname.startsWith(`${portalRoutes.welcomePartner}/`);

/**
 * Returns the menu items for the footer.
 * @param t - The translation function.
 * @returns An array of objects containing the label and link of the menu items.
 */
export const getMenuItems = (t: TFunction, pathname = "") => {
  const portalSwitchItem = isPartnerWelcomePath(pathname)
    ? {
        label: t("Footer.customerPortal.label"),
        link: `#${portalRoutes.welcome}`,
      }
    : {
        label: t("Footer.partnerPortal.label"),
        link: `#${portalRoutes.welcomePartner}`,
      };

  return [
    { label: t("Footer.website.label"), link: t("Footer.website.url") },
    portalSwitchItem,
  ];
};

/**
 * This component is used to display the link menu in the footer.
 * It is a collapsible accordion-style menu that displays the various website links.
 * The menu can be toggled by clicking on the logo label.
 **/
export const CollapsibleLinkMenu = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const menuItems = useMemo(() => getMenuItems(t, pathname), [t, pathname]);

  return (
    <Collapsible id="link-menu" label={t("Common.logoName")}>
      {menuItems.map(({ label, link }) => (
        <NavItemLink key={label} label={label} link={link} inverted />
      ))}
    </Collapsible>
  );
};
